import React, { memo } from "react";
import {
  Card,
  CardContent,
  Stack,
  Box,
  Typography,
  type CardProps,
} from "@mui/material";

export interface IconInfoCardProps extends Omit<CardProps, "children"> {
  icon: React.ReactNode;
  title: string;
  color?: "primary" | "secondary" | "success" | "warning";
  children: React.ReactNode;
}

const IconInfoCard: React.FC<IconInfoCardProps> = memo(
  ({ icon, title, color = "primary", children, sx, ...cardProps }) => {
    const borderColorKey =
      color === "primary"
        ? "primary.light"
        : color === "secondary"
        ? "secondary.light"
        : color === "success"
        ? "success.light"
        : "warning.light";

    const iconColorKey =
      color === "primary"
        ? "primary.main"
        : color === "secondary"
        ? "secondary.main"
        : color === "success"
        ? "success.main"
        : "warning.main";

    return (
      <Card
        elevation={3}
        sx={{
          height: "100%",
          p: 3,
          display: "flex",
          flexDirection: "column",
          gap: 2,
          ...sx,
        }}
        {...cardProps}
      >
        <CardContent
          sx={{
            p: 0,
            "&:last-child": { pb: 0 },
            display: "flex",
            flexDirection: "column",
            gap: 2,
          }}
        >
          <Stack direction="row" spacing={1.5} alignItems="center" sx={{ color: iconColorKey }}>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: "50%",
                border: "2px solid",
                borderColor: borderColorKey,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {icon}
            </Box>
            <Typography variant="h6" fontWeight={600}>
              {title}
            </Typography>
          </Stack>

          <Box>{children}</Box>
        </CardContent>
      </Card>
    );
  },
);

IconInfoCard.displayName = "IconInfoCard";

export default IconInfoCard;

